import React, { useState } from 'react';
import { X, MessageCircle } from 'lucide-react';

const WHATSAPP_NUMBER = '918015600003';
const DEFAULT_MESSAGE = 'Hello! I am interested in learning about your solar power solutions for my business.';

const WhatsAppWidget = () => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState(DEFAULT_MESSAGE);

  const handleSend = () => {
    const encoded = encodeURIComponent(message);
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encoded}`, '_blank');
    setOpen(false);
  };

  return (
    <div className="fixed bottom-20 right-6 z-40" data-testid="whatsapp-widget">
      {/* Chat Popup */}
      {open && (
        <div
          className="mb-4 w-80 bg-white rounded-lg shadow-2xl border border-border overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-300"
          data-testid="whatsapp-popup"
        >
          {/* Header */}
          <div className="bg-[#075E54] px-4 py-3 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center overflow-hidden">
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}/api/files/invictus-green/uploads/974218eb-5249-49d9-9fa5-bef235aac409.png`}
                  alt="IGES"
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <p className="text-white text-sm font-semibold leading-tight">Invictus Green Energy</p>
                <p className="text-white/70 text-xs">Typically replies instantly</p>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="text-white/80 hover:text-white transition-colors"
              data-testid="whatsapp-close"
              aria-label="Close chat"
            >
              <X size={20} />
            </button>
          </div>

          {/* Chat Body */}
          <div className="bg-[#ECE5DD] p-4 min-h-[120px]">
            <div className="bg-white rounded-lg p-3 shadow-sm max-w-[85%] relative">
              <p className="text-sm text-gray-800 leading-relaxed">
                Hi there! How can we help you with your solar energy requirements today?
              </p>
              <span className="text-[10px] text-gray-400 mt-1 block text-right">IGES</span>
              <div className="absolute -left-2 top-3 w-3 h-3 bg-white transform rotate-45"></div>
            </div>
          </div>

          {/* Input */}
          <div className="p-3 bg-white border-t border-border">
            <div className="flex gap-2">
              <input
                type="text"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Type a message..."
                className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-full focus:outline-none focus:ring-1 focus:ring-[#25D366] focus:border-[#25D366]"
                onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                data-testid="whatsapp-input"
              />
              <button
                onClick={handleSend}
                className="w-10 h-10 bg-[#25D366] rounded-full flex items-center justify-center hover:bg-[#20BD5A] transition-colors shadow-md"
                data-testid="whatsapp-send"
                aria-label="Send on WhatsApp"
              >
                <svg viewBox="0 0 24 24" width="20" height="20" fill="white">
                  <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floating Button */}
      <button
        onClick={() => setOpen(!open)}
        className="w-14 h-14 bg-[#25D366] rounded-full flex items-center justify-center shadow-lg hover:shadow-xl hover:scale-105 transition-all group"
        data-testid="whatsapp-toggle"
        aria-label="Chat on WhatsApp"
      >
        {open ? (
          <X size={24} className="text-white" />
        ) : (
          <svg viewBox="0 0 32 32" width="28" height="28" fill="white">
            <path d="M16.004 0h-.008C7.174 0 0 7.176 0 16c0 3.5 1.13 6.742 3.05 9.376L1.054 31.5l6.31-1.966C10.012 31.08 12.904 32 16.004 32 24.826 32 32 24.822 32 16S24.826 0 16.004 0zm9.35 22.606c-.39 1.1-1.932 2.012-3.172 2.278-.848.18-1.956.324-5.684-1.222-4.772-1.978-7.838-6.82-8.076-7.138-.228-.318-1.918-2.554-1.918-4.872s1.214-3.456 1.644-3.928c.43-.472.94-.59 1.254-.59.314 0 .628.002.902.016.29.016.68-.11 1.062.81.392.944 1.332 3.262 1.45 3.498.118.236.196.512.04.826-.158.318-.236.514-.472.792-.236.278-.496.62-.71.832-.236.236-.482.492-.208.964.274.472 1.218 2.01 2.616 3.256 1.798 1.602 3.314 2.098 3.784 2.334.472.236.746.196 1.02-.118.274-.314 1.176-1.372 1.49-1.844.314-.472.628-.392 1.06-.236.432.158 2.746 1.296 3.218 1.532.472.236.786.354.904.55.118.196.118 1.138-.274 2.236z" />
          </svg>
        )}

        {/* Pulse animation */}
        {!open && (
          <span className="absolute -top-1 -right-1 flex h-4 w-4">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#25D366] opacity-75"></span>
            <span className="relative inline-flex rounded-full h-4 w-4 bg-[#128C7E]"></span>
          </span>
        )}
      </button>
    </div>
  );
};

export default WhatsAppWidget;
